// src/services/firebase.ts
import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, type User as FirebaseUser } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

declare global {
  // Prevent duplicate connections when React refreshes modules in development.
  var __CLASS_TECH_FIREBASE_EMULATORS_CONNECTED__: boolean | undefined;
  var __CLASS_TECH_FIREBASE_APPCHECK_INITIALIZED__: boolean | undefined;
  var FIREBASE_APPCHECK_DEBUG_TOKEN: boolean | string | undefined;
  var __CLASS_TECH_AUTH_DIAGNOSTIC_SEQ__: number | undefined;
  var __CLASS_TECH_AUTH_DIAGNOSTIC_MARKERS__: WeakMap<object, string> | undefined;
}

const authDiagnosticsEnabled = process.env.REACT_APP_AUTH_DIAGNOSTICS === 'true';

const useFirebaseEmulators =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true';
const emulatorProjectId =
  process.env.REACT_APP_FIREBASE_EMULATOR_PROJECT_ID || 'demo-class-tech';
const appCheckSiteKey = process.env.REACT_APP_FIREBASE_APPCHECK_SITE_KEY;
const isLocalDevelopmentHost =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname);
const appCheckDebugToken = process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN;
const localAppCheckDebugToken =
  typeof window !== 'undefined'
    ? window.localStorage.getItem('CLASS_TECH_APPCHECK_DEBUG_TOKEN') || ''
    : '';
const shouldGenerateAppCheckDebugToken =
  process.env.NODE_ENV !== 'production' &&
  process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG === 'true';
const useAppCheckDebugProvider = Boolean(
  appCheckDebugToken ||
    localAppCheckDebugToken ||
    shouldGenerateAppCheckDebugToken
);
const shouldInitializeAppCheck =
  !useFirebaseEmulators &&
  Boolean(appCheckSiteKey) &&
  (!isLocalDevelopmentHost || useAppCheckDebugProvider);

const firebaseConfig = {
  apiKey:
    (useFirebaseEmulators
      ? 'demo-api-key'
      : process.env.REACT_APP_FIREBASE_API_KEY) ||
    'AIzaSyA15CIwM6Vs9YE8bh4LfWy0MCnYmAf94tw',
  authDomain:
    (useFirebaseEmulators
      ? `${emulatorProjectId}.firebaseapp.com`
      : process.env.REACT_APP_FIREBASE_AUTH_DOMAIN) ||
    'class-tech-9c860.firebaseapp.com',
  projectId:
    (useFirebaseEmulators
      ? emulatorProjectId
      : process.env.REACT_APP_FIREBASE_PROJECT_ID) || 'class-tech-9c860',
  storageBucket:
    (useFirebaseEmulators
      ? `${emulatorProjectId}.appspot.com`
      : process.env.REACT_APP_FIREBASE_STORAGE_BUCKET) ||
    'class-tech-9c860.firebasestorage.app',
  messagingSenderId:
    process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '258884542711',
  appId:
    process.env.REACT_APP_FIREBASE_APP_ID ||
    '1:258884542711:web:e5489978b7fd73196173f2',
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

const authDiagnosticMarker = `firebase-module-${
  (globalThis.__CLASS_TECH_AUTH_DIAGNOSTIC_SEQ__ || 0) + 1
}`;
globalThis.__CLASS_TECH_AUTH_DIAGNOSTIC_SEQ__ = Number(
  authDiagnosticMarker.replace('firebase-module-', '')
);
const authDiagnosticMarkers =
  globalThis.__CLASS_TECH_AUTH_DIAGNOSTIC_MARKERS__ ||
  new WeakMap<object, string>();
globalThis.__CLASS_TECH_AUTH_DIAGNOSTIC_MARKERS__ = authDiagnosticMarkers;

const authObjectMarker = (value: object): string => {
  const existing = authDiagnosticMarkers.get(value);
  if (existing) return existing;
  const marker = `auth-${(globalThis.__CLASS_TECH_AUTH_DIAGNOSTIC_SEQ__ || 0) + 1}`;
  globalThis.__CLASS_TECH_AUTH_DIAGNOSTIC_SEQ__ = Number(
    marker.replace('auth-', '')
  );
  authDiagnosticMarkers.set(value, marker);
  return marker;
};

const authErrorCategory = (error: unknown): string => {
  if (!error || typeof error !== 'object') return 'unknown';
  const candidate = error as { code?: unknown; name?: unknown };
  return String(candidate.code || candidate.name || 'unknown');
};

if (
  shouldInitializeAppCheck &&
  !globalThis.__CLASS_TECH_FIREBASE_APPCHECK_INITIALIZED__
) {
  if (useAppCheckDebugProvider) {
    globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN =
      appCheckDebugToken || localAppCheckDebugToken || true;
    if (!appCheckDebugToken && !localAppCheckDebugToken) {
      console.info(
        'Firebase App Check debug token generation is enabled. Register the printed debug token in Firebase Console, then store it in localStorage as CLASS_TECH_APPCHECK_DEBUG_TOKEN.'
      );
    }
  }

  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(appCheckSiteKey!),
    isTokenAutoRefreshEnabled: true,
  });

  globalThis.__CLASS_TECH_FIREBASE_APPCHECK_INITIALIZED__ = true;
} else if (!useFirebaseEmulators && !appCheckSiteKey) {
  console.warn(
    'Firebase App Check is not initialized because REACT_APP_FIREBASE_APPCHECK_SITE_KEY is missing. Add the reCAPTCHA v3 site key for the current Firebase App Check app before deploying with App Check enforcement.'
  );
} else if (!useFirebaseEmulators && isLocalDevelopmentHost) {
  console.info(
    'Firebase App Check is disabled on localhost. To call App Check protected functions locally, register a debug token in Firebase Console and store it as CLASS_TECH_APPCHECK_DEBUG_TOKEN in localStorage.'
  );
}

// Initialize Firebase services
export const auth = getAuth(app);

let latestAuthUser: FirebaseUser | null = auth.currentUser;

const getCurrentUserUid = (): string | null => auth.currentUser?.uid || null;

export const authDiagnostic = (
  event: string,
  data: Record<string, unknown> = {}
): void => {
  if (!authDiagnosticsEnabled) return;
  const sequence = (globalThis.__CLASS_TECH_AUTH_DIAGNOSTIC_SEQ__ || 0) + 1;
  globalThis.__CLASS_TECH_AUTH_DIAGNOSTIC_SEQ__ = sequence;
  console.info(
    `[AUTH_DIAG] ${JSON.stringify({
      sequence,
      event,
      appName: app.name,
      projectId: app.options.projectId || null,
      appId: app.options.appId || null,
      authInstance: authObjectMarker(auth),
      moduleMarker: authDiagnosticMarker,
      authCurrentUserUid: getCurrentUserUid(),
      canonicalUserUid: latestAuthUser?.uid || null,
      ...data,
    })}`
  );
};

authDiagnostic('FIREBASE_APP_INITIALIZED');
authDiagnostic('AUTH_SINGLETON_CREATED');

// AuthContext is the one Auth observer. It publishes the exact Firebase user
// object it received so code-split repositories cannot observe a stale/null
// currentUser during Auth restoration.
export const setCanonicalFirebaseUser = (user: FirebaseUser | null): void => {
  latestAuthUser = user;
  authDiagnostic('CANONICAL_USER_PUBLISHED', {
    publishedUserUid: user?.uid || null,
  });
};

export const getAuthenticatedFirebaseUser = async (): Promise<FirebaseUser> => {
  authDiagnostic('TOKEN_HELPER_INVOCATION');
  const user = latestAuthUser || auth.currentUser;
  if (!user) {
    authDiagnostic('TOKEN_HELPER_USER_RESULT', {
      success: false,
      errorCategory: 'UNAUTHENTICATED',
    });
    throw new Error('UNAUTHENTICATED');
  }
  authDiagnostic('TOKEN_HELPER_USER_RESULT', {
    success: true,
    resolvedUserUid: user.uid,
  });
  return user;
};

export const getAuthenticatedIdToken = async (): Promise<string> => {
  authDiagnostic('TOKEN_HELPER_GET_ID_TOKEN_START');
  try {
    const token = await getAuthenticatedFirebaseUser().then(user => user.getIdToken());
    authDiagnostic('TOKEN_HELPER_GET_ID_TOKEN_RESULT', { success: true });
    return token;
  } catch (error) {
    authDiagnostic('TOKEN_HELPER_GET_ID_TOKEN_RESULT', {
      success: false,
      errorCategory: authErrorCategory(error),
    });
    throw error;
  }
};

export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

// Initialize messaging (for notifications)
let messaging: any = null;
if (!useFirebaseEmulators) {
  try {
    messaging = getMessaging(app);
  } catch (error) {
    console.log('Messaging not supported in this environment:', error);
  }
}

export { messaging };

if (
  useFirebaseEmulators &&
  !globalThis.__CLASS_TECH_FIREBASE_EMULATORS_CONNECTED__
) {
  const host = process.env.REACT_APP_FIREBASE_EMULATOR_HOST || '127.0.0.1';

  connectAuthEmulator(auth, `http://${host}:9099`, {
    disableWarnings: true,
  });
  connectFirestoreEmulator(db, host, 8080);
  connectFunctionsEmulator(functions, host, 5001);
  connectStorageEmulator(storage, host, 9199);

  globalThis.__CLASS_TECH_FIREBASE_EMULATORS_CONNECTED__ = true;
  console.info(`Firebase emulators connected on ${host}`);
}

// Messaging functions
export const requestNotificationPermission = async (): Promise<
  string | null
> => {
  if (!messaging) return null;

  try {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      });
      return token;
    }
    return null;
  } catch (error) {
    console.error('Error getting notification permission:', error);
    return null;
  }
};

export const onMessageListener = (): Promise<any> =>
  new Promise(resolve => {
    if (!messaging) return;

    onMessage(messaging, payload => {
      resolve(payload);
    });
  });

export default app;
